import React, { useEffect, useState, useRef } from 'react'
import Text from 'particles/Text'
import styled from '../themes/styled'
import RectangleButton, { RectangleButtonProps } from 'particles/Button/RectangleButton'
import Icon, { IconName } from 'particles/Icon'
import IconButton from 'particles/Button/IconButton'

export type ModalSize = 's' | 'm' | 'l'

interface ModalContainerProps {
    size: ModalSize
    isButtonSetFixed: boolean
    isTitleOverflowed: boolean
}

export interface ModalProps {
    onClose?: React.MouseEventHandler<HTMLButtonElement>
    onConfirm?: React.MouseEventHandler<HTMLButtonElement>
    size: ModalSize
    title: string
    subtext?: string
    hasSecondaryButton?: boolean
    hasButtonSet?: boolean
    primaryButtonProps?: RectangleButtonProps
    secondaryButtonProps?: RectangleButtonProps
    isClosable?: boolean
    isButtonSetFixed?: boolean
    icon?: IconName
    hasIcon?: boolean
    img?: string
    className?: string
    fitPrimaryButton?: boolean
}

const sizeMap = {
    s: {
        style: {
            maxWidth: '444px',
            minWidth: '343px',
            width: '30%',
            maxHeight: '62%',
        },
    },
    m: {
        style: {
            maxWidth: '600px',
            minWidth: '444px',
            width: '42%',
            maxHeight: '74%',
        },
    },
    l: {
        style: {
            maxWidth: '900px',
            minWidth: '600px',
            width: '62.5%',
            maxHeight: '94%',
        },
    },
}

export const modalZIndex = 999

const Modal: React.FC<ModalProps> = ({
    onClose,
    onConfirm,
    size = 'l',
    title,
    subtext,
    hasSecondaryButton = true,
    hasButtonSet = true,
    primaryButtonProps,
    secondaryButtonProps,
    isClosable = true,
    isButtonSetFixed = false,
    icon = 'warningCircle',
    hasIcon = true,
    img,
    fitPrimaryButton = false,
    className,
    children,
}) => {
    const [isTitleOverflowed, setIsTitleOverflowed] = useState(false)
    const modalHeaderRef = useRef<HTMLDivElement | null>(null)
    const contentRef = useRef<HTMLDivElement | null>(null)

    const handleCloseModal = (e: any): void => {
        if (e.target.id === 'modal-container') {
            onClose?.(e)
        }
    }

    useEffect(() => {
        if (!isClosable) return
        window.addEventListener('click', handleCloseModal)
        return (): void => window.removeEventListener('click', handleCloseModal)
    }, [])

    useEffect(() => {
        if (modalHeaderRef.current && contentRef.current) {
            setIsTitleOverflowed(modalHeaderRef.current.scrollHeight > contentRef.current.scrollHeight - 200)
        }
    }, [title])

    const modalFooter = hasButtonSet ? (
        <div className={`modal-footer ${fitPrimaryButton && !hasSecondaryButton ? 'fit-button' : ''}`}>
            {hasSecondaryButton ? (
                <RectangleButton
                    {...secondaryButtonProps}
                    size="l"
                    form="outlined"
                    onClick={onClose}
                    data-testid={'test-secondary-modal-button'}
                />
            ) : null}
            <RectangleButton
                {...primaryButtonProps}
                size="l"
                onClick={onConfirm}
                data-testid={'test-primary-modal-button'}
            />
        </div>
    ) : null

    return (
        <ModalContainer
            className={className}
            size={size}
            isButtonSetFixed={isButtonSetFixed}
            isTitleOverflowed={isTitleOverflowed}
            id="modal-container"
        >
            <div className="modal-content" ref={contentRef}>
                {isClosable ? (
                    <IconButton
                        className="close-button"
                        onClick={onClose}
                        icon="x"
                        size="l"
                        color="neutral"
                        form="icon"
                    />
                ) : null}
                <div className="scrollable-content-container">
                    <div className="modal-header" ref={modalHeaderRef}>
                        {img ? (
                            <img alt="modal-image" src={img} className="modal-image" />
                        ) : icon && hasIcon ? (
                            <Icon className="modal-icon" name={icon} size="40px" color="primary" />
                        ) : null}
                        <Text scale="H3" color="primary" className="modal-title">
                            {title}
                        </Text>
                    </div>
                    <div className="modal-body">
                        <div className="component-body-container">
                            {subtext ? (
                                <Text scale="bodyM400" className="modal-subtext">
                                    {subtext}
                                </Text>
                            ) : (
                                <div />
                            )}
                            {children}
                        </div>
                        {!isButtonSetFixed ? modalFooter : null}
                    </div>
                </div>
                {isButtonSetFixed ? modalFooter : null}
            </div>
        </ModalContainer>
    )
}

const ModalContainer = styled.div<ModalContainerProps>`
    position: fixed;
    width: 100%;
    height: 100vh;
    top: 0;
    left: 0;
    z-index: ${modalZIndex};
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;

    .modal-content {
        ${({ theme }) => ({
            background: theme.colors.background.secondary,
        })}
        ${({ size }) => sizeMap[size ?? 'l'].style}
        border: 0;
        box-sizing: border-box;
        box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
        border-radius: 4px;
        display: flex;
        justify-content: space-between;
        flex-direction: column;
        position: relative;
        padding: 40px 24px 24px;
        @media screen and (max-width: 768px) {
            min-width: 0;
            width: calc(100% - 32px);
            max-height: 70%;
        }

        .scrollable-content-container {
            ${({ isTitleOverflowed }) => ({
                display: isTitleOverflowed ? 'block' : 'contents',
                overflowY: isTitleOverflowed ? 'auto' : 'initial',
            })}
        }

        .modal-header {
            word-break: break-word;
            text-wrap: wrap;
        }

        .modal-image {
            display: block;
            max-width: 100%;
            margin: 16px auto;
        }

        .modal-body {
            overflow-y: auto;
            .modal-subtext {
                margin-top: 8px;
            }
            .component-body-container {
                display: flex;
                flex-direction: column;
                gap: ${({ theme }) => theme.spacing[600]};
            }
        }

        .modal-footer {
            width: 100%;
            display: flex;
            justify-content: flex-end;
            margin-top: 24px;
            gap: ${({ theme }) => theme.spacing[500]};
            @media screen and (max-width: 768px) {
                ${({ theme }) => ({
                    gap: theme.spacing[400],
                })};
            }
        }
        .fit-button {
            gap: 0;
            button {
                width: 100%;
            }
        }
        .close-button {
            position: absolute;
            right: 12px;
            top: 12px;
        }
    }
`

export default Modal
